/**
 * PinShownOnceBanner — D-30 reveal-on-create / reveal-on-reset.
 *
 * CP4 invariants:
 * - PIN is passed in via props from the parent modal's state (never from cache).
 * - PIN is NOT stored in localStorage / sessionStorage / Query cache.
 * - No console.log of the PIN value.
 * - navigator.clipboard.writeText is operator-initiated (accepted boundary T-2-CP4-WK3).
 */
import { useState } from "react";
import { Copy } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { Button } from "../../components/Button";

interface PinShownOnceBannerProps {
  pin: string;
  onClose: () => void;
}

export function PinShownOnceBanner({ pin, onClose }: PinShownOnceBannerProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write failed silently — operator can read the PIN on screen.
    }
  };

  return (
    <div
      role="alert"
      data-testid="pin-shown-once-banner"
      className="bg-warning-container border-s-4 border-warning ps-6 pe-6 pbs-6 pbe-6 rounded-md inline-size-[480px] mx-auto"
    >
      <div className="flex flex-col gap-4 text-center">
        <p className="text-sm font-semibold text-warning">
          <FormattedMessage id="WARN_PIN_KEPT_ONCE" values={{ pin }} />
        </p>
        <div
          className="text-4xl font-mono font-semibold tracking-widest text-warning"
          data-testid="pin-display"
          aria-label={pin}
        >
          {pin}
        </div>
        {copied && (
          <p className="text-sm text-on-secondary-container" role="status">
            <FormattedMessage id="OK_PIN_COPIED" />
          </p>
        )}
        <div className="flex gap-3 justify-center">
          <Button
            variant="primary"
            onClick={onClose}
            data-testid="pin-banner-done-button"
          >
            <FormattedMessage id="LABEL_DONE" />
          </Button>
          <button
            type="button"
            onClick={handleCopy}
            data-testid="pin-copy-button"
            className="inline-flex items-center gap-2 ps-4 pe-4 h-11 bg-surface-raised border border-primary text-primary font-semibold rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors duration-200"
          >
            <Copy size={16} aria-hidden="true" />
            <FormattedMessage id="LABEL_COPY_PIN" />
          </button>
        </div>
      </div>
    </div>
  );
}
